import { css, cx } from 'linaria';
import { cssTheme } from '@uds/theme-utils';
import { Maybe } from 'core';

const rootWrapper = css`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.gu1};
`;

const root = css`
  width: 100%;
  padding: ${cssTheme.gu3};
  background-color: white;
  border-radius: ${cssTheme.radius.panel};
  border: 1px solid ${cssTheme.palette.grey10};
  display: flex;
  gap: ${cssTheme.gu3};
  flex-direction: column;

  ${cssTheme.breakpoints.md} {
    flex-direction: row;
    align-items: center;
  }
  ${cssTheme.breakpoints.lg} {
    padding: ${cssTheme.gu4};
  }
`;

const domainNameExtension = css`
  font-weight: bold;
`;

const saleBadge = css`
  display: flex;
`;

const saleBadgeNewline = css`
  flex-direction: column;

  ${cssTheme.breakpoints.sm} {
    flex-direction: row;
  }
`;

const captionPrice = css`
  display: flex;
  flex-direction: row;
  gap: ${cssTheme.gu1};
  align-items: center;
  flex-wrap: wrap;
  ${cssTheme.breakpoints.md} {
    justify-content: flex-end;
  }
`;

const pricePerYear = css`
  color: ${cssTheme.palette.primary};
  font-size: ${cssTheme.typography.sizes.s};
  line-height: ${cssTheme.typography.lineHeight.s};
  font-family: ${cssTheme.font.display};

  ${cssTheme.breakpoints.sm} {
    font-size: ${cssTheme.typography.sizes.m};
    line-height: ${cssTheme.typography.lineHeight.m};
  }

  ${cssTheme.breakpoints.md} {
    font-size: ${cssTheme.typography.sizes.l};
    line-height: ${cssTheme.typography.lineHeight.l};
  }
`;

const price = css`
  font-size: ${cssTheme.typography.sizes.xs};
  line-height: ${cssTheme.typography.lineHeight.xs};
  font-family: ${cssTheme.font.content};
  display: flex;
  align-items: center;
  gap: ${cssTheme.gu1};
`;

const priceWrap = css`
  display: flex;
  align-items: center;
  & * {
    ${cssTheme.breakpoints.md} {
      justify-content: flex-end;
      text-align: end;
    }
    flex-wrap: wrap;
  }
`;

const priceBefore = css`
  text-decoration: line-through;
`;

const saleBadgeInfoText = css`
  padding: ${cssTheme.gu1} ${cssTheme.gu4};
`;

const badgesWrapper = css`
  display: flex;
  gap: ${cssTheme.gu2};

  ${cssTheme.breakpoints.sm} {
    margin-left: auto;
  }
`;

const hideBadge = css`
  display: none !important;
`;

const domainNameWrapper = css`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.gu3};
  word-break: break-word;

  ${cssTheme.breakpoints.sm} {
    align-items: center;
    flex-direction: row-reverse;
    justify-content: start;
  }
`;

const displayRowSm = css`
  ${cssTheme.breakpoints.sm} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const ctaWrapper = css`
  display: flex;
  flex-basis: 0%;
  flex-shrink: 1;
  flex-grow: 1;
  flex-direction: column;
  gap: ${cssTheme.gu3};

  ${cssTheme.breakpoints.sm} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  ${cssTheme.breakpoints.md} {
    margin-left: auto;
    justify-content: flex-end;
  }
`;

const priceAndBadgeWrapper = css`
  display: flex;
  gap: ${cssTheme.gu2};
  flex-wrap: wrap;
  justify-content: flex-start;
  ${cssTheme.breakpoints.md} {
    justify-content: flex-end;
  }
`;

const purchaseGroupMarginLeftAuto = css`
  ${cssTheme.breakpoints.sm} {
    margin-left: auto;
  }
`;

const buttonWrapper = css`
  ${cssTheme.breakpoints.sm} {
    align-self: flex-end;
  }
  ${cssTheme.breakpoints.md} {
    align-self: center;
  }
`;

const recurringPricePromo = css`
  margin-bottom: 0;
`;

export const getClassNames = ({
  oldFirstYearPrice,
  firstYearPrice,
  hidePrices,
}: {
  oldFirstYearPrice: Maybe<number>;
  firstYearPrice: Maybe<number>;
  hidePrices: boolean;
}) => {
  const shouldBreakPriceLine =
    (oldFirstYearPrice != null && oldFirstYearPrice > 999) || (firstYearPrice != null && firstYearPrice > 9999);

  return {
    rootWrapper,
    root: cx(root, hidePrices && displayRowSm),
    domainNameExtension,
    captionPrice,
    price,
    priceWrap,
    pricePerYear,
    priceBefore,
    priceAndBadgeWrapper: cx(priceAndBadgeWrapper, shouldBreakPriceLine && saleBadgeNewline),
    saleBadge,
    badgesWrapper,
    saleBadgeInfoText,
    hideBadge,
    ctaWrapper,
    domainNameWrapper,
    recurringPricePromo,
    purchaseGroup: cx(hidePrices && purchaseGroupMarginLeftAuto, buttonWrapper),
  };
};
